import { api, ApiError } from "@/lib/api";
import type { MemoryProposeResult } from "@/lib/customer-memory";

/** 客户资料面板展示用的关联 Wiki 页摘要 */
export type ContactWikiPage = {
  id: number;
  wiki_id: number;
  title: string;
  slug: string;
  page_type: string;
  brand_id: number | null;
};

export type WecomFollowProfile = {
  userid: string;
  remark: string | null;
  description: string | null;
  remark_corp_name: string | null;
  remark_mobiles: string[];
  tags: { id?: number | string | null; name: string; group_name: string | null }[];
  add_way?: number | null;
  createtime?: number | null;
};

export type WecomContactDetail = {
  follow_profiles: WecomFollowProfile[];
};

export type WecomExternalContact = {
  id: number;
  external_userid: string;
  name: string | null;
  avatar_url: string | null;
  gender: number | null;
  contact_type: number | null;
  follow_userids: string[];
  /** 客户记忆热快照；空表示尚无沉淀 */
  memory_summary: string | null;
  /** 显式关联的 Kb::Page id */
  wiki_page_ids?: number[];
  /** show 时解析后的关联页（含 frontmatter.external_userid 约定） */
  wiki_pages?: ContactWikiPage[];
  /** show/update：企微备注、描述、标签等（来自上次同步/编辑拉取） */
  wecom_detail?: WecomContactDetail;
  /** 合并后的企微标签 id（可筛选） */
  wecom_tag_ids?: string[];
  /** 合并后的企微标签展示 */
  wecom_tags?: WecomFollowProfile["tags"];
  /** 最近一次相关入站回调时间（物化列） */
  last_inbound_at: string | null;
  /** 账户级置顶时间；非空即置顶 */
  pinned_at: string | null;
  /** 是否置顶（与 pinned_at 同步） */
  pinned: boolean;
  /** 流失时刻；有值=不可达流失，null=未流失/已恢复 */
  churned_at: string | null;
  updated_at: string;
};

/** 客户列表排序：默认最近入站；置顶始终优先 */
export type WecomExternalContactsSort = "last_activity" | "created_at" | "name";

/** 对齐服务端 ordered_for_list：置顶优先，再按 sort 次级键 */
export function sortWecomExternalContactsForList(
  contacts: WecomExternalContact[],
  sort: WecomExternalContactsSort = "last_activity",
): WecomExternalContact[] {
  const pinRank = (c: WecomExternalContact) => (c.pinned || c.pinned_at ? 0 : 1);
  const pinnedAtMs = (c: WecomExternalContact) =>
    c.pinned_at ? Date.parse(c.pinned_at) || 0 : 0;
  const inboundMs = (c: WecomExternalContact) =>
    c.last_inbound_at ? Date.parse(c.last_inbound_at) || 0 : Number.NEGATIVE_INFINITY;
  const nameKey = (c: WecomExternalContact) => (c.name ?? "").toLocaleLowerCase();

  return [...contacts].sort((a, b) => {
    const pinDiff = pinRank(a) - pinRank(b);
    if (pinDiff !== 0) return pinDiff;
    if (pinRank(a) === 0) {
      const pinnedDiff = pinnedAtMs(b) - pinnedAtMs(a);
      if (pinnedDiff !== 0) return pinnedDiff;
    }

    if (sort === "name") {
      const nameDiff = nameKey(a).localeCompare(nameKey(b), "zh-CN");
      if (nameDiff !== 0) return nameDiff;
      return a.id - b.id;
    }

    if (sort === "created_at") {
      // API 未下发 created_at；同页内用 id 近似（与服务端 id DESC 并列键一致）
      return b.id - a.id;
    }

    // last_activity：无消息沉底
    const inboundDiff = inboundMs(b) - inboundMs(a);
    if (inboundDiff !== 0) return inboundDiff;
    return b.id - a.id;
  });
}

/** 用 PATCH 返回行替换本页项并按当前排序重排（避免整页重拉导致滚动跳动） */
export function mergeAndSortPinnedContact(
  contacts: WecomExternalContact[],
  updated: WecomExternalContact,
  sort: WecomExternalContactsSort = "last_activity",
): WecomExternalContact[] {
  return sortWecomExternalContactsForList(
    contacts.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    sort,
  );
}

/** 工作台主区滚动容器（WorkspaceShell `@container/main`） */
export function workspaceMainScrollElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[class*="@container/main"]');
}

/** 布局重排后恢复 overflow 容器 scrollTop（同步一次 + 双 rAF 兜底） */
export function restoreElementScrollTop(el: HTMLElement | null | undefined, scrollTop: number) {
  if (!el) return;
  const apply = () => {
    el.scrollTop = scrollTop;
  };
  apply();
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
}

export type WecomExternalContactsPage = {
  external_contacts: WecomExternalContact[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
};

export function genderLabel(gender: number | null | undefined): string {
  if (gender === 1) return "男";
  if (gender === 2) return "女";
  return "未知";
}

export function contactTypeLabel(type: number | null | undefined): string {
  if (type === 1) return "微信";
  if (type === 2) return "企微";
  return "—";
}

/** 企微标签展示：有分组则「分组/名」 */
export function formatWecomTagLabel(tag: {
  name: string;
  group_name?: string | null;
}): string {
  const name = tag.name?.trim() || "";
  const group = tag.group_name?.trim();
  if (!name) return "";
  return group ? `${group}/${name}` : name;
}

/** 最近入站/互动时间副标题；无则诚实空态 */
export function formatLastInboundHint(
  iso: string | null | undefined,
  emptyLabel = "暂无互动",
): string {
  if (!iso) return emptyLabel;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return emptyLabel;
  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 60_000) return "刚刚";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)} 分钟前`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)} 小时前`;
  if (diffMs < 7 * 86_400_000) return `${Math.floor(diffMs / 86_400_000)} 天前`;
  return date.toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" });
}

export type WecomTagOption = {
  id: string;
  name: string;
  group_name: string | null;
};

export type WecomExternalContactsListFilters = {
  page?: number;
  perPage?: number;
  q?: string;
  followUserids?: string[];
  /** 企微标签 id（OR：命中任一） */
  wecomTagIds?: string[];
  /** 1 男 / 2 女 / 0 未知（含 NULL）；省略=全部 */
  gender?: 0 | 1 | 2;
  /** 默认 last_activity */
  sort?: WecomExternalContactsSort;
};

export async function listWecomExternalContacts(
  accountId: string | number,
  opts?: WecomExternalContactsListFilters,
): Promise<WecomExternalContactsPage> {
  const page = opts?.page ?? 1;
  const perPage = opts?.perPage ?? 20;
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.followUserids?.length) {
    for (const userid of opts.followUserids) {
      params.append("follow_userids[]", userid);
    }
  }
  if (opts?.wecomTagIds?.length) {
    for (const tagId of opts.wecomTagIds) {
      params.append("wecom_tag_ids[]", tagId);
    }
  }
  if (opts?.gender !== undefined) {
    params.set("gender", String(opts.gender));
  }
  if (opts?.sort) {
    params.set("sort", opts.sort);
  }

  return api.fetchForAccount<WecomExternalContactsPage>(
    accountId,
    `/account/wecom_external_contacts?${params.toString()}`,
  );
}

/** 账户内已出现过的企微标签（筛选下拉） */
export async function listWecomExternalContactTags(
  accountId: string | number,
): Promise<WecomTagOption[]> {
  const payload = await api.fetchForAccount<{ tags: WecomTagOption[] }>(
    accountId,
    "/account/wecom_external_contacts/tags",
  );
  return payload.tags ?? [];
}

export async function getWecomExternalContact(
  accountId: string | number,
  contactId: string | number,
): Promise<WecomExternalContact> {
  const payload = await api.fetchForAccount<{ external_contact: WecomExternalContact }>(
    accountId,
    `/account/wecom_external_contacts/${contactId}`,
  );
  return payload.external_contact;
}

/** 更新客户资料偏好（账户级置顶 / 确认后的客户记忆） */
export async function updateWecomExternalContact(
  accountId: string | number,
  contactId: string | number,
  attrs: { pinned?: boolean; memory_summary?: string | null },
): Promise<WecomExternalContact> {
  const payload = await api.fetchForAccount<{ external_contact: WecomExternalContact }>(
    accountId,
    `/account/wecom_external_contacts/${contactId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ external_contact: attrs }),
    },
  );
  return payload.external_contact;
}

/** 描述事实 → AI 策展提案（不落库） */
export async function proposeWecomExternalContactMemory(
  accountId: string | number,
  contactId: string | number,
  facts: string,
): Promise<MemoryProposeResult> {
  return api.fetchForAccount<MemoryProposeResult>(
    accountId,
    `/account/wecom_external_contacts/${contactId}/memory/propose`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ facts }),
    },
  );
}

export { ApiError };
